/**
 * useContactsSelection — the contacts-table selection model, extracted from
 * `ImportDetailPage` for the Gmail-style cross-page select-all (epic cl.4,
 * sub-task 05).
 *
 * Two modes:
 *
 * - `explicit` — a set of checked `import_contact_id`s, exactly the model the
 *   page kept inline before this hook existed. Covers "nothing selected"
 *   (empty set) and every by-hand selection, including the page checkbox.
 *   Explicit ids deliberately SURVIVE filter and page changes — that is the
 *   pre-existing behaviour ("selectedIds persists across pagination",
 *   `ImportDetailPage.tsx`) and this hook does not change it.
 *
 * - `all-filtered` — "every contact matching the ACTIVE filters", represented
 *   as the FILTER SCOPE itself (`filtersKey` + the server `total` captured at
 *   escalation), never as a materialized id list: a 1,300-contact list is
 *   selected without fetching 1,300 ids. Entered only via
 *   `selectAllFiltered()` (the banner). While in this mode `selectedIds` is
 *   EMPTY on purpose — every downstream consumer that reads an empty explicit
 *   selection (Compare scope, the narrowing fetch) already treats it as
 *   whole-import semantics, which is what an unfiltered all-filtered
 *   selection IS. Writes must NOT read `selectedIds` in this mode; they ride
 *   the filter-scoped request shape (`select_all: true`).
 *
 * UNCHECK-ONE RULE (the PRD's requirement 6, decided here): unchecking a
 * single row while in `all-filtered` mode DROPS TO EXPLICIT mode — the
 * selection becomes the currently visible selectable rows minus the unchecked
 * one, and the cross-page claim is gone. This is option (a), and it is what
 * Gmail itself does ("Select all conversations that match" + uncheck one →
 * back to a plain checkbox selection). Chosen over (b) exclusion-recording
 * because exclusions cannot ride the existing write shape
 * (`_ExportSelectionRequest` has no `exclude_ids` arm), so recording one
 * client-side would either need a new API arm or silently not exclude — the
 * exact dishonesty this sub-task exists to prevent.
 *
 * STALE-N INVALIDATION: any change to the selection's universe clears
 * `all-filtered` mode (back to explicit-empty) — the captured N is no longer
 * a claim we can stand behind. Two triggers, both checked in an effect:
 *
 * - `filtersKey` changed (a filter, the search box, or the import itself —
 *   the caller folds `importId` into the key). Sort is deliberately NOT part
 *   of the key: reordering does not change membership, so it must not eat the
 *   selection.
 * - the live server `total` for the same key drifted from the total captured
 *   at escalation (rows were added by a parse, removed by a delete — the
 *   dataset changed under the claim).
 *
 * Explicit selections are untouched by both triggers (pre-existing
 * behaviour; stale ids simply stop matching any row).
 */
import { useCallback, useEffect, useState } from "react";

export type ContactsSelectionMode = "explicit" | "all-filtered";

/** The "nothing is selected" identity — one instance, so an empty selection
 *  is referentially stable across renders (same trick as `ContactsTable`'s
 *  `EMPTY_SELECTION`). */
const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

type SelectionState =
  | { mode: "explicit"; ids: ReadonlySet<string> }
  | { mode: "all-filtered"; capturedKey: string; capturedTotal: number };

const NOTHING: SelectionState = { mode: "explicit", ids: EMPTY_IDS };

export interface UseContactsSelectionArgs {
  /**
   * Identity of the selection's universe: the import id + the MEMBERSHIP
   * filters (not sort), serialized by the caller. Any change invalidates a
   * cross-page selection.
   */
  filtersKey: string;
  /** Live server `total` for the active filters; `undefined` while loading.
   *  The banner's N, and the drift detector for a stale cross-page claim. */
  total: number | undefined;
}

export interface ContactsSelection {
  mode: ContactsSelectionMode;
  /** Explicit ids. EMPTY while in `all-filtered` mode — see the docblock. */
  selectedIds: ReadonlySet<string>;
  /** What the selection means for a row's checkbox. In `all-filtered` mode
   *  every row is selected. */
  isSelected: (id: string) => boolean;
  /** What the footer CTA should count: explicit size, or the live filtered
   *  total in `all-filtered` mode (0 while it is still loading). */
  selectedCount: number;
  /** One row's checkbox. In `all-filtered` mode this is the drop-to-explicit
   *  transition: the selection becomes `visibleSelectableIds` minus `id`. */
  toggle: (id: string, visibleSelectableIds: readonly string[]) => void;
  /** The page checkbox: add/remove a whole page of ids. In `all-filtered`
   *  mode, unchecking clears everything (the page checkbox reads checked
   *  there, so unchecking it is "deselect all"); checking is a no-op
   *  (everything already is). */
  setPage: (ids: readonly string[], select: boolean) => void;
  /** Escalate to "all N matching the active filters". No-op while the total
   *  is unknown — there is no N to claim. */
  selectAllFiltered: () => void;
  /** Back to nothing selected, whatever the mode. */
  clear: () => void;
}

export function useContactsSelection({
  filtersKey,
  total,
}: UseContactsSelectionArgs): ContactsSelection {
  const [state, setState] = useState<SelectionState>(NOTHING);

  // Stale-N invalidation. An effect rather than a render-time derivation so
  // the clear is a real state transition the mode-machine tests can observe,
  // and so it fires exactly when the inputs change (filter edit, refetch)
  // rather than on every render.
  useEffect(() => {
    setState((prev) => {
      if (prev.mode !== "all-filtered") return prev;
      const keyChanged = filtersKey !== prev.capturedKey;
      const totalDrifted = total !== undefined && total !== prev.capturedTotal;
      return keyChanged || totalDrifted ? NOTHING : prev;
    });
  }, [filtersKey, total]);

  const toggle = useCallback((id: string, visibleSelectableIds: readonly string[]) => {
    setState((prev) => {
      if (prev.mode === "all-filtered") {
        // Drop-to-explicit (the documented uncheck-one rule): keep what is
        // on screen, minus the row just unchecked. The cross-page claim is
        // dropped rather than amended — see the file docblock.
        const ids = new Set(visibleSelectableIds);
        ids.delete(id);
        return { mode: "explicit", ids };
      }
      const ids = new Set(prev.ids);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      return { mode: "explicit", ids };
    });
  }, []);

  const setPage = useCallback((ids: readonly string[], select: boolean) => {
    setState((prev) => {
      if (prev.mode === "all-filtered") {
        // Checking the page checkbox in all-filtered mode asks for what is
        // already true; unchecking it is "deselect everything".
        return select ? prev : NOTHING;
      }
      const next = new Set(prev.ids);
      for (const id of ids) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return { mode: "explicit", ids: next };
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    // No N, no claim: the banner should not render without a total, but a
    // caller invoking this early must not create a mode with nothing behind it.
    if (total === undefined) return;
    setState({ mode: "all-filtered", capturedKey: filtersKey, capturedTotal: total });
  }, [filtersKey, total]);

  const clear = useCallback(() => setState(NOTHING), []);

  const inAllFiltered = state.mode === "all-filtered";
  const selectedIds = state.mode === "explicit" ? state.ids : EMPTY_IDS;

  const isSelected = useCallback(
    (id: string) => (inAllFiltered ? true : selectedIds.has(id)),
    [inAllFiltered, selectedIds]
  );

  return {
    mode: state.mode,
    selectedIds,
    isSelected,
    selectedCount: inAllFiltered ? (total ?? 0) : selectedIds.size,
    toggle,
    setPage,
    selectAllFiltered,
    clear,
  };
}
